import { useQuery } from 'react-query';
import { useMemo, useContext, createContext } from 'react';
import cookies from 'browser-cookies';
import { dispatchGAEvent } from '@uds/tagmg';
import { invariant } from '@ux/js';
import { jsx, Fragment } from 'react/jsx-runtime';
import { pipe, json, jsonRpc, bodyOnly, fetch } from '@ux/drivers';
import { useWindow } from '@uds/hooks';

const appConfig$1 = apps => ['@ux/whitelabel/app-config', apps];

const REDUCER_MOUNT_POINT = 'whitelabel';
const EXPERIMENT_COOKIE_KEY = 'bpt_experiment_id';
const BRAND_ID_COOKIE = 'brand_id';
const MARKET_COOKIE = 'market';
const SEED_COOKIE = 'bpt_Seed';
const BRAND_API_URL = '/api/brand-pilot';
const COOKIE_LIFESPAN = 30;

const makeFetchAppConfig = (driver, getReferrer, getUserAgent, window, dispatchEvent, getBrandId, getSeed, getExperiment, saveExperiment) => async apps => {
  var _config$meta$experime, _config, _config$meta;
  const experiment = getExperiment();
  const getConfigFromCache = async () => {
    var _window$hugBootstrap;
    const promise = window.hugPromise;
    if (promise == null) {
      return null;
    }
    await promise;
    const config = (_window$hugBootstrap = window.hugBootstrap) === null || _window$hugBootstrap === void 0 ? void 0 : _window$hugBootstrap.brandPilot;
    if (config == null) {
      return null;
    }
    return config;
  };
  const saveAsyncConfig = async promise => {
    window.hugPromise = promise;
    const result = await promise;
    if (!window.hugBootstrap) {
      window.hugBootstrap = {};
    }
    window.hugBootstrap.brandPilot = result;
    return result;
  };
  const getConfigFromApi = async () => {
    const url = BRAND_API_URL;
    const userAgent = getUserAgent();
    const referrer = getReferrer();
    const brandId = getBrandId();
    const keys = apps;
    const seed = getSeed();
    const messages = false;
    const location = {
      hash: window.location.hash,
      host: window.location.host,
      hostname: window.location.hostname,
      href: window.location.href,
      origin: window.location.origin,
      pathname: window.location.pathname,
      port: window.location.port,
      protocol: window.location.protocol,
      search: window.location.search
    };
    const data = {
      brandId,
      keys,
      userAgent,
      referrer,
      experiment,
      seed,
      messages,
      location
    };
    Object.keys(data).forEach(key => {
      if (data[key] == null) {
        delete data[key];
      }
    });
    const request = {
      url,
      method: 'v1.getMultiConfig',
      data
    };
    const rttStart = window.performance.now();
    const result = await saveAsyncConfig(driver(request));
    const rttEnd = window.performance.now();
    const rttDiff = rttEnd - rttStart;
    dispatchEvent({
      hitType: 'timing',
      timingCategory: 'API RTT',
      timingLabel: 'Brand Pilot',
      timingVar: 'load',
      timingValue: Math.round(rttDiff)
    });
    return result;
  };
  let config = await getConfigFromCache();
  if (config == null) {
    config = await getConfigFromApi();
  }
  saveExperiment((_config$meta$experime = (_config = config) === null || _config === void 0 ? void 0 : (_config$meta = _config.meta) === null || _config$meta === void 0 ? void 0 : _config$meta.experiment) != null ? _config$meta$experime : null);
  return config;
};
var makeFetchAppConfig$1 = makeFetchAppConfig;

var driver = (() => pipe(json(), jsonRpc(), bodyOnly())(fetch));

const fetchBrandId = cookies => () => {
  var _cookies$get;
  return (_cookies$get = cookies.get(BRAND_ID_COOKIE)) != null ? _cookies$get : '';
};
var fetchBrandId$1 = fetchBrandId;

const fetchMarket = cookies => () => {
  var _cookies$get;
  return (_cookies$get = cookies.get(MARKET_COOKIE)) != null ? _cookies$get : '';
};
var fetchMarket$1 = fetchMarket;

const getReferrer = document => () => document.referrer;
var getReferrer$1 = getReferrer;

const generate = () => Math.floor(Math.random() * 65536);
var getSeed = (cookies => () => {
  let value = cookies.get(SEED_COOKIE);
  if (value == null) {
    value = generate();
    cookies.set(SEED_COOKIE, value.toString(), {
      expires: COOKIE_LIFESPAN
    });
  }
  return Number(value);
});

const getUserAgent = window => () => {
  var _window$navigator$use, _window$navigator;
  return (_window$navigator$use = window === null || window === void 0 ? void 0 : (_window$navigator = window.navigator) === null || _window$navigator === void 0 ? void 0 : _window$navigator.userAgent) != null ? _window$navigator$use : '';
};
var getUserAgent$1 = getUserAgent;

var getExperiment = (cookies => () => {
  var _cookies$get;
  const str = (_cookies$get = cookies.get(EXPERIMENT_COOKIE_KEY)) != null ? _cookies$get : 'null';
  return JSON.parse(str);
});

var saveExperiment = (cookies => experiment => {
  if (experiment == null) {
    return;
  }
  const str = JSON.stringify({
    id: experiment.id,
    variant: experiment.variant
  });
  cookies.set(EXPERIMENT_COOKIE_KEY, str);
});

const WhitelabelContext = /*#__PURE__*/createContext(undefined);
const WhitelabelProvider = _ref => {
  let {
    children,
    apps,
    useLanguage
  } = _ref;
  const state = useMemo(() => {
    invariant(dispatchGAEvent != null, 'Expected dispatchGAEvent to be defined');
    return {
      apps,
      useLanguage,
      market: fetchMarket$1(cookies)(),
      brand: fetchBrandId$1(cookies)(),
      fetchAppConfig: makeFetchAppConfig$1(driver(), getReferrer$1(document), getUserAgent$1(window), window, dispatchGAEvent, fetchBrandId$1(cookies), getSeed(cookies), getExperiment(cookies), saveExperiment(cookies))
    };
  }, [apps, useLanguage]);
  return /*#__PURE__*/jsx(WhitelabelContext.Provider, {
    value: state,
    children: children
  });
};
const useWhitelabelContext = () => {
  const context = useContext(WhitelabelContext);
  if (context == null) {
    throw new Error('Expected context to be defined, but it was not, did you forget to add the WhitelabelProvider?');
  }
  return context;
};
const useApps = () => {
  const context = useWhitelabelContext();
  return context.apps;
};

const useConfigFn = (apps, fetchAppConfig) => {
  return () => {
    return fetchAppConfig(apps);
  };
};
const useConfigQuery = _ref => {
  let {
    opts,
    useGetApps = useApps,
    useGetConfigFn = useConfigFn,
    fetchAppConfigFn
  } = _ref;
  const apps = useGetApps();
  const fn = useGetConfigFn(apps, fetchAppConfigFn);
  const key = appConfig$1(apps);
  const enabled = apps != null;
  return useQuery(key, fn, {
    enabled,
    staleTime: Infinity,
    ...opts
  });
};
function useUseConfigQuery(opts) {
  const {
    fetchAppConfig
  } = useWhitelabelContext();
  return useConfigQuery({
    opts,
    fetchAppConfigFn: fetchAppConfig
  });
}

const useAppConfig = function (app) {
  var _config$data, _config$data$apps;
  let useGetConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : useUseConfigQuery;
  const config = useGetConfig();
  return (_config$data = config.data) === null || _config$data === void 0 ? void 0 : (_config$data$apps = _config$data.apps) === null || _config$data$apps === void 0 ? void 0 : _config$data$apps[app];
};
function useUseAppConfig(app) {
  return useAppConfig(app);
}

/* istanbul ignore next */
const useBrandConfig = () => useUseAppConfig('brand');
var useBrandConfig$1 = useBrandConfig;

/* istanbul ignore file */
var useAssets = (() => {
  var _useBrandConfig;
  return (_useBrandConfig = useBrandConfig$1()) === null || _useBrandConfig === void 0 ? void 0 : _useBrandConfig.assets;
});

const useBrandId = () => {
  const {
    brand
  } = useWhitelabelContext();
  return brand;
};
var useBrandId$1 = useBrandId;

/* istanbul ignore next */
var useBrandLogos = (() => {
  return useAssets().brandLogos;
});

const useLocales = () => {
  const {
    navigator
  } = useWindow();
  return navigator.languages;
};
const useLooseLocales = () => {
  const locales = useLocales();
  return useMemo(() => {
    return locales.map(locale => locale.split('-')[0]).filter((locale, i, arr) => arr.indexOf(locale) === i);
  }, [locales]);
};

/* istanbul ignore next */
const useLocale = () => useLocales()[0];

/* istanbul ignore next */
const useLooseLocale = () => useLooseLocales()[0];

const useMarket = () => {
  const {
    market
  } = useWhitelabelContext();
  return market;
};
var useMarket$1 = useMarket;

var defaultTheme = {
  palette: {
    headerBackground: '#777',
    headerForeground: '#505050',
    headerHighlight: '',
    primary: '#505050',
    primaryContrast: '',
    primaryGradient: '#505050',
    secondary: '#505050',
    tertiary: '#777',
    highlight: '',
    highlightContrast: '',
    accent: '#505050',
    contrast: '#fff',
    contrastAccent: '#fff',
    cta: '',
    ctaContrast: '',
    illustrations: {
      primary: '',
      primaryContrast: '',
      secondary: '',
      secondaryContrast: '',
      tertiary: '',
      tertiaryContrast: ''
    },
    grey01: '#1a1a1a',
    grey02: '#282828',
    grey03: '#333',
    grey04: '#484848',
    grey05: '#666',
    grey06: '#848484',
    grey07: '#909090',
    grey08: '#999',
    grey09: '#ccc',
    grey10: '#eee',
    grey11: '#f2f2f2',
    grey12: '#f7f7f7',
    red01: '#cc0000',
    red02: '#f4c7c7',
    yellow01: '#EDD000',
    yellow02: '#FAF1B2',
    green01: '#7fa800',
    green02: '#d8e5b2'
  },
  typography: {
    content: 'Arial, sans-serif',
    display: 'Arial, sans-serif',
    contentURL: null,
    displayURL: null,
    sizing: 1
  },
  brandLogos: {
    logoLight: '',
    logoDark: '',
    logoContrast: ''
  },
  curvature: 0,
  spacing: 0,
  iconSet: ''
};

const useTheme = function () {
  let useGetBrandConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : useBrandConfig$1;
  const brandConfig = useGetBrandConfig();
  const brandTheme = brandConfig === null || brandConfig === void 0 ? void 0 : brandConfig.theme;
  const theme = useMemo(() => {
    return {
      ...defaultTheme,
      ...brandTheme,
      palette: {
        ...defaultTheme.palette,
        ...(brandTheme === null || brandTheme === void 0 ? void 0 : brandTheme.palette)
      }
    };
  }, [brandTheme]);
  return theme;
};
function useUseTheme() {
  return useTheme();
}

/* istanbul ignore file */
var useUrls = (() => useBrandConfig$1().urls);

const useUrl = function (key) {
  let useGetUrls = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : useUrls;
  const {
    useLanguage
  } = useWhitelabelContext();
  const {
    data: language
  } = useLanguage({
    suspense: true
  });
  const urls = useGetUrls();
  const props = [`${language}.${key}`, `${key}.${language}`, key];
  return props.reduce((acc, prop) => acc != null ? acc : urls[prop], undefined);
};
function useUseUrl(key) {
  return useUrl(key);
}

function useTealiumCategories(options) {
  var _useConfig, _useConfig$data;
  const categories = (_useConfig = useUseConfigQuery(options)) === null || _useConfig === void 0 ? void 0 : (_useConfig$data = _useConfig.data) === null || _useConfig$data === void 0 ? void 0 : _useConfig$data.apps.brand.analytics.tealium.privacy.categories;
  return useMemo(() => {
    const categoriesWithEssentials = Array.from(new Set(['essential', ...(categories != null ? categories : [])]));
    return categoriesWithEssentials.map(category => category === 'advertising' ? 'marketing' : category);
  }, [categories]);
}

const useWhitelabel = function () {
  let useGetConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : useUseConfigQuery;
  const {
    status
  } = useGetConfig();
  const ready = status === 'success';
  return [ready];
};
function useUseWhitelabel() {
  return useWhitelabel();
}

const brandConfig = {
  aklamioId: 'aklamio-id',
  plid: 'plid',
  analytics: {
    tealium: {
      privacy: {
        categories: ['analytics', 'marketing']
      },
      profile: 'mint'
    }
  },
  assets: {
    brandLogos: {
      logoContrast: 'contrast',
      logoDark: 'dark',
      logoLight: 'light'
    },
    favicon: 'favicon.ico'
  },
  basket: {
    multiQuantity: false
  },
  domain: 'mint.co.uk',
  languages: ['en-gb', 'de-de'],
  ecommerce: true,
  locale: {
    default: 'en'
  },
  payment: {
    addTaxToDisplayPrice: true,
    currencies: ['GBP', 'EUR'],
    currency: 'GBP',
    supportedMethods: [],
    // @ts-ignore - previous whitelabel had incorrect type for this property
    transparentPricing: true
  },
  social: {},
  support: {
    email: 'support@mint.co.uk',
    phone: '+441234567890',
    url: 'https://mint.co.uk/support'
  },
  // @ts-ignore: get's added later
  theme: null,
  trustPilot: {},
  urls: {}
};
const appConfig = {
  brand: brandConfig
};

const getAppConfig = appConfigProp => {
  if (typeof appConfigProp === 'function') {
    return appConfigProp(appConfig);
  }
  if (appConfigProp == null) {
    return appConfig;
  }
  return appConfigProp;
};
const useLanguageFn = () => {
  return useQuery('@ux/whitelabel-useLanguage', () => 'en-GB');
};
const Provider = _ref => {
  let {
    apps = ['brand'],
    useLanguage = useLanguageFn,
    children,
    market = 'GB',
    brandId = 'mint',
    ...props
  } = _ref;
  const state = useMemo(() => {
    const fetchAppConfig = () => Promise.resolve({
      apps: getAppConfig(props.appConfig)
    });
    return {
      apps,
      useLanguage,
      market,
      brand: brandId,
      fetchAppConfig
    };
  }, [props.appConfig, apps, useLanguage, market, brandId]);
  return /*#__PURE__*/jsx(WhitelabelContext.Provider, {
    value: state,
    children: /*#__PURE__*/jsx(TestProvider, {
      ...props,
      children: children
    })
  });
};
const TestProvider = _ref2 => {
  let {
    appConfig,
    children
  } = _ref2;
  useUseConfigQuery({
    initialData: {
      apps: getAppConfig(appConfig)
    },
    staleTime: 1000 * 60 * 5
  });
  useUseWhitelabel();
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
};
var Provider$1 = Provider;

export { BRAND_API_URL, BRAND_ID_COOKIE, COOKIE_LIFESPAN, EXPERIMENT_COOKIE_KEY, MARKET_COOKIE, Provider$1 as Provider, REDUCER_MOUNT_POINT, SEED_COOKIE, WhitelabelProvider, appConfig, brandConfig, useUseAppConfig as useAppConfig, useApps, useAssets, useBrandConfig$1 as useBrandConfig, useBrandId$1 as useBrandId, useBrandLogos, useUseConfigQuery as useConfig, useLocale, useLocales, useLooseLocale, useLooseLocales, useMarket$1 as useMarket, useTealiumCategories, useUseTheme as useTheme, useUseUrl as useUrl, useUrls, useUseWhitelabel as useWhitelabel };
